import React, { useState, useRef, useEffect } from "react";
import "./login.css";
import logo1 from '../assets/CPA1.png'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faGraduationCap } from "@fortawesome/free-solid-svg-icons";
import Toast from "./Toast";
import API_BASE_URL from "../apiConfig";

export default function Login({ setIsAuthenticated }) {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  // FORGOTTEN PASSWORD: there is no self-service reset on this app, so the
  // link opens a small "Contact your Admin" popup instead of doing nothing
  // (it used to be a bare href="#forgot", which only jumped the page).
  const [showForgotPopup, setShowForgotPopup] = useState(false);
  const userIdRef = useRef(null);
  const forgotTriggerRef = useRef(null);
  const forgotOkRef = useRef(null);
  const previousFocusRef = useRef(null);

  const handleMoodleLogin = () => {
    const returnUrl = window.location.origin + window.location.pathname;
    window.location.href = `${API_BASE_URL}/api/moodle_sso_login.php?return_to=${encodeURIComponent(returnUrl)}`;
  };

  useEffect(() => {
    // 1. Process Moodle SAML SSO Return (URL Hash / Search Params)
    try {
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      let token = null;
      let userData = null;

      if (hash.includes("token=")) {
        const hashParams = new URLSearchParams(hash.replace(/^#/, "?"));
        token = hashParams.get("token");
        const rawUser = hashParams.get("user");
        if (rawUser) {
          try {
            userData = JSON.parse(atob(decodeURIComponent(rawUser)));
          } catch (e1) {
            try {
              userData = JSON.parse(decodeURIComponent(rawUser));
            } catch (e2) { /* ignore */ }
          }
        }
      } else if (search.includes("token=")) {
        const searchParams = new URLSearchParams(search);
        token = searchParams.get("token");
        const rawUser = searchParams.get("user");
        if (rawUser) {
          try {
            userData = JSON.parse(atob(decodeURIComponent(rawUser)));
          } catch (e1) {
            try {
              userData = JSON.parse(decodeURIComponent(rawUser));
            } catch (e2) { /* ignore */ }
          }
        }
      }

      if (token) {
        localStorage.setItem("token", token);
        if (userData) {
          localStorage.setItem("user", JSON.stringify(userData));
        }
        if (window.history.replaceState) {
          window.history.replaceState(null, null, window.location.pathname);
        }
        setToast({ message: "Moodle SSO Login successful", type: "success" });
        setTimeout(() => {
          if (setIsAuthenticated) {
            setIsAuthenticated(true);
          }
          // Let the reminder popup know this is a fresh login so it can show
          // right after the app loads (never on the login screen itself).
          sessionStorage.setItem("reminderShowOnLoad", "1");
          window.location.href = "/Home";
        }, 1200);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const ssoError = urlParams.get("error") || urlParams.get("message");
      if (ssoError && search.includes("sso=")) {
        setToast({ message: decodeURIComponent(ssoError), type: "error" });
      }
    } catch (err) {
      console.error("Error processing SSO callback:", err);
    }

    if (userIdRef.current) {
      userIdRef.current.focus();
    }
  }, [setIsAuthenticated]);

  // WCAG 2.4.3 / 2.1.1: dialog focus management - no visual change, only focus behavior
  useEffect(() => {
    if (showForgotPopup) {
      previousFocusRef.current = document.activeElement;
      const id = window.setTimeout(() => {
        if (forgotOkRef.current) forgotOkRef.current.focus();
      }, 0);
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          setShowForgotPopup(false);
        }
        // Focus trap inside dialog (Tab / Shift+Tab)
        if (e.key === "Tab") {
          const dialog = document.getElementById("forgot-dialog-panel");
          if (!dialog) return;
          const focusable = dialog.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.clearTimeout(id);
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = prevOverflow;
      };
    } else {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        const trigger = previousFocusRef.current;
        window.setTimeout(() => trigger.focus(), 0);
      }
    }
  }, [showForgotPopup]);



  const validate = () => {
    let newErrors = {};
    if (!userId.trim()) newErrors.userId = "User ID is required";
    if (!password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/therapist_api/therapist_login.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            moodle_id: userId,
            password: password,
          }),
        });
        
        const data = await response.json();
        
        if (data.status) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
          setToast({ message: "Login successful", type: "success" });
          setTimeout(() => {
            if (setIsAuthenticated) {
              setIsAuthenticated(true);
            }
            // Let the reminder popup know this is a fresh login so it can show
            // right after the app loads (never on the login screen itself).
            sessionStorage.setItem("reminderShowOnLoad", "1");
            window.location.href = "/Home";
          }, 1500);
        } else {
          setToast({ message: data.message || "Invalid User credentials", type: "error" });
          setErrors({ password: data.message || "Invalid credentials" });
        }
      } catch (error) {
        console.error("Login error:", error);
        setToast({ message: "Invalid User credentials", type: "error" });
        setErrors({ password: "An error occurred during login" });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      {/* The app shell (App.js) owns the only skip link and the only <main>
          landmark. This page is a labelled section inside it, so a screen
          reader hears one main landmark instead of "main landmark 1 / 2". */}
      {/* WCAG 4.1.3 Status Messages - sr-only live region, no visual impact */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {isLoading ? "Logging in, please wait." : ""}
      </div>
      <section className="login-container body1" aria-labelledby="login-heading">
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
        <div className="login-box">
          <div className="logo">
            <img src={logo1} className="logo1" alt="CPA Logo" />
          </div>
          <h1 id="login-heading" className="login-title">Login</h1>
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="login-userid">User ID</label>
              <input
                ref={userIdRef}
                id="login-userid"
                name="username"
                type="text"
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                required
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  if (errors.userId) setErrors((prev) => { const n = { ...prev }; delete n.userId; return n; });
                }}
                placeholder="Enter User ID"
                className="user_inp"
                aria-required="true"
                aria-invalid={errors.userId ? "true" : "false"}
                aria-describedby={errors.userId ? "userid-error" : undefined}
              />
              {errors.userId && <span className="error" id="userid-error" role="alert">{errors.userId}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <div className="password-field">
                <input
                  id="login-password"
                  name="current-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => { const n = { ...prev }; delete n.password; return n; });
                  }}
                  placeholder="Enter Password"
                  className="user_inp"
                  aria-required="true"
                  aria-invalid={errors.password ? "true" : "false"}
                  aria-describedby={errors.password ? "password-error" : undefined}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  aria-controls="login-password"
                >
                  <FontAwesomeIcon className="login-icon" icon={showPassword ? faEye : faEyeSlash} aria-hidden="true" />
                </button>
              </div>
              {errors.password && <span className="error" id="password-error" role="alert">{errors.password}</span>}
              <div className="forgot">
                {/* WCAG 4.1.2 / 2.1.1: button (not anchor) for action; styled to look exactly like original link */}
                <button
                  ref={forgotTriggerRef}
                  type="button"
                  className="forgot-link"
                  onClick={() => setShowForgotPopup(true)}
                  aria-haspopup="dialog"
                  aria-expanded={showForgotPopup ? "true" : "false"}
                  aria-controls="forgot-dialog-panel"
                >
                  Forgot password
                </button>
              </div>
            </div>
            <button type="submit" className="login-btn" disabled={isLoading} aria-busy={isLoading ? "true" : "false"}>
              {isLoading ? "Logging in..." : "Login"}
            </button>

            <div className="login-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="moodle-btn"
              onClick={handleMoodleLogin}
              title="Sign in with Moodle"
            >
              <FontAwesomeIcon icon={faGraduationCap} className="moodle-icon" aria-hidden="true" />
              <span>Login with Moodle</span>
            </button>
          </form>
        </div>

        {/* -- FORGOTTEN PASSWORD popup --------------------------------------
            Passwords on this system are managed by the administrator (Moodle /
            admin-issued accounts), so there is no reset form to send the user
            to - the popup says who to contact instead of leaving the link dead.
            Visual styles kept 100% identical via inline styles; only ARIA/focus added.
            Dismissed by the OK button, the backdrop, or Escape. */}
        {showForgotPopup && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-popup-title"
            aria-describedby="forgot-popup-desc"
            onClick={() => setShowForgotPopup(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
            }}
          >
            <div
              id="forgot-dialog-panel"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "26px 30px",
                maxWidth: "420px",
                width: "100%",
                boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <h3
                id="forgot-popup-title"
                style={{ margin: 0, color: "var(--cpa-primary, #247A3D)", fontSize: "20px", fontWeight: 700, lineHeight: 1.35 }}
              >
                Contact the Admin
              </h3>
              <p id="forgot-popup-desc" style={{ margin: 0, color: "#626A6F", fontSize: "15px", lineHeight: 1.7 }}>
                 Please contact
                your Administrator to reset your password.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  ref={forgotOkRef}
                  type="button"
                  className="login-btn"
                  style={{ width: "auto", padding: "8px 22px" }}
                  onClick={() => setShowForgotPopup(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
